import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { SHIPPING_NOTICE, CONTACT_INFO, SHIPPING_METHOD, SHIPPING_TIME, FREE_SHIPPING_THRESHOLD, siteImages } from '../data/mock';
import { Truck, Clock, Globe, Package, Phone, Mail, Flame, Zap, MapPin, AlertCircle } from 'lucide-react';

const ShippingPage = () => {
  useEffect(() => {
    document.title = 'Shipping Information | Axon Muscle';
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.shopBanner}
              alt="Shipping"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/95 via-neutral-950/85 to-neutral-950/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
          </div>
          
          {/* Red Glow */}
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-axon-600/20 rounded-full blur-[120px]" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-6">
              <Truck className="w-4 h-4" />
              DELIVERY INFO
            </div>
            <h1 className="font-display text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
              Shipping Information
            </h1>
            <p className="text-neutral-300 text-lg max-w-2xl mx-auto">
              Fast, reliable shipping to fuel your performance.
            </p>
          </div>

          {/* Promo Banner */}
          <div className="relative bg-gradient-to-r from-axon-700 via-axon-600 to-axon-700 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-center gap-2 text-white font-semibold text-sm lg:text-base">
                <Truck className="w-4 h-4" />
                {SHIPPING_NOTICE} | FREE Shipping on Orders ${FREE_SHIPPING_THRESHOLD}+
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Shipping Method Banner */}
            <div className="bg-gradient-to-r from-axon-600 to-axon-500 text-white rounded-3xl p-8 mb-10 shadow-glow-cyan">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <Truck className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-display font-bold uppercase tracking-wide">We Ship via {SHIPPING_METHOD}</h3>
                  <p className="text-axon-200">United States Postal Service</p>
                </div>
              </div>
              <p className="text-lg text-axon-100">
                All orders are shipped through {SHIPPING_METHOD} with delivery tracking provided.
              </p>
            </div>

            {/* Shipping Options */}
            <div className="grid md:grid-cols-2 gap-6 mb-16">
              <div className="bg-neutral-900 rounded-2xl p-8 border border-neutral-800 hover:border-axon-600/50 transition-all">
                <div className="w-14 h-14 bg-axon-600/20 rounded-xl flex items-center justify-center mb-6">
                  <Truck className="w-7 h-7 text-axon-500" />
                </div>
                <h3 className="font-display text-xl text-white uppercase tracking-wide mb-2">Standard Shipping</h3>
                <p className="text-3xl font-bold text-axon-500 mb-4">$9.95</p>
                <p className="text-sm mb-2 font-semibold text-axon-400">FREE on orders over ${FREE_SHIPPING_THRESHOLD}</p>
                <p className="text-neutral-400">Delivery in {SHIPPING_TIME}</p>
              </div>
              
              <div className="bg-neutral-900 rounded-2xl p-8 border border-neutral-800 hover:border-axon-600/50 transition-all">
                <div className="w-14 h-14 bg-neon-500/20 rounded-xl flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-neon-500" />
                </div>
                <h3 className="font-display text-xl text-white uppercase tracking-wide mb-2">Processing Time</h3>
                <p className="text-3xl font-bold text-neon-500 mb-4">1-2 Days</p>
                <p className="text-neutral-400">Orders are processed Monday through Friday</p>
              </div>
            </div>

            {/* Shipping Details */}
            <div className="bg-neutral-900 rounded-3xl border border-neutral-800 p-8 lg:p-10 mb-10">
              <h2 className="font-display text-2xl text-white uppercase tracking-wide mb-6">Shipping Details</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 pb-6 border-b border-neutral-800">
                  <div className="w-12 h-12 bg-axon-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-axon-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Shipping Coverage</h3>
                    <p className="text-neutral-400">We currently ship within the United States only. International shipping is not available at this time.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 pb-6 border-b border-neutral-800">
                  <div className="w-12 h-12 bg-axon-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-axon-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Order Tracking</h3>
                    <p className="text-neutral-400">Once your order ships, you'll receive an email with tracking information so you can monitor your delivery status.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-axon-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-axon-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Delivery Address</h3>
                    <p className="text-neutral-400">Please ensure your shipping address is complete and accurate. We are not responsible for packages delivered to incorrect addresses provided by the customer.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-axon-600/10 border border-axon-500/30 rounded-2xl p-6 mb-10">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-axon-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-axon-400 mb-1">Important Notice</h4>
                  <p className="text-axon-300 text-sm">
                    Delivery times are estimates and may vary based on carrier conditions, weather, and other factors beyond our control. We are not responsible for delays caused by the shipping carrier.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-neutral-900 rounded-2xl border border-neutral-800 p-8 text-center">
              <h3 className="font-display font-bold text-white uppercase tracking-wide mb-4">Questions About Shipping?</h3>
              <p className="text-neutral-400 mb-6">Our customer support team is here to help.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a 
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-2 text-axon-500 hover:text-axon-400 transition-colors font-medium"
                >
                  <Phone className="w-5 h-5" />
                  {CONTACT_INFO.phone}
                </a>
                <a 
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-2 text-axon-500 hover:text-axon-400 transition-colors font-medium"
                >
                  <Mail className="w-5 h-5" />
                  {CONTACT_INFO.email}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShippingPage;
